import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Breadcrumbs, CircularProgress, Link as MuiLink, Typography } from '@mui/material';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';
import { useGetAccountAssets } from '~/hooks/apis/offers/useGetAccountAssets';

import RoomAssetDetailPanel from '../Assets/RoomAssetDetailPanel';
import SellableAssetDetailPanel from '../Assets/SellableAssetDetailPanel';

import { accountPath } from './accountRelatedModel';
import { useAccountOfferId } from './useAccountOfferId';

export default function AccountAssetDetailPage() {
  const { id_account: accountId, assetId } = useParams<{ id_account: string; assetId: string }>();
  const offerId = useAccountOfferId();
  const { data: account } = useGetAccount(accountId);
  const rooms = useGetAccountAssets(accountId, 'rooms');
  const services = useGetAccountAssets(accountId, 'services');
  const roomAsset = rooms.data?.find((asset) => asset.sfid === assetId);
  const serviceAsset = services.data?.find((asset) => asset.sfid === assetId);
  const asset = roomAsset ?? serviceAsset;
  const isLoading = rooms.isLoading || services.isLoading;
  const error = rooms.error ?? services.error;
  const name = asset?.name || assetId;

  return (
    <>
      <Helmet>
        <title>
          {name} | {account?.name || accountId}
        </title>
      </Helmet>

      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to={accountPath(accountId, offerId)} underline="hover">
          Related
        </MuiLink>
        <MuiLink component={Link} to={accountPath(accountId, offerId, '/assets')} underline="hover">
          Assets
        </MuiLink>
        <Typography color="text.primary">{name}</Typography>
      </Breadcrumbs>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {name}
      </Typography>

      {roomAsset ? (
        <RoomAssetDetailPanel hideName hideSalesforceLinks roomAsset={roomAsset} />
      ) : serviceAsset ? (
        <SellableAssetDetailPanel asset={serviceAsset} hideName hideSalesforceLinks />
      ) : isLoading ? (
        <CircularProgress size={24} />
      ) : error ? (
        <Alert severity="error">Could not load assets: {error.message}</Alert>
      ) : (
        <Alert severity="warning">No active asset with id {assetId} on this Account.</Alert>
      )}
    </>
  );
}
